import React, { useState } from "react";
import axios from "axios";
import { Crown, Check, Calendar, Users, Clock, ExternalLink, Loader2 } from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import { Reveal } from "../hooks/useReveal";
import { useToast } from "../hooks/use-toast";
import { BRAND, IMAGES, BOTTLE_PACKAGES } from "../mock";
import { useReservation } from "../components/ReservationModal";
import { DatePickerField, TimePickerField } from "../components/DateTimeFields";
import OpenTableWidget from "../components/OpenTableWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Reservations() {
  const { toast } = useToast();
  const { open: openReservation } = useReservation();
  const [form, setForm] = useState({ name: "", email: "", phone: "", date: "", time: "", guests: "2", occasion: "", notes: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/reservations`, { ...form, source: "reservations-page" });
      setSubmitted(true);
      toast({ title: "Reservation Request Received", description: `Thank you, ${form.name}. Our team will confirm your table shortly.` });
    } catch (err) {
      toast({ title: "Submission Failed", description: `Please call us at ${BRAND.phone}.`, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-nala-charcoal border border-nala-gold/25 text-nala-cream px-4 py-3 focus:outline-none focus:border-nala-gold transition-colors placeholder:text-nala-cream-muted/50";

  return (
    <div className="pt-28 pb-24 bg-nala-black min-h-screen">
      {/* Banner */}
      <div className="relative h-64 md:h-80 overflow-hidden mb-16">
        <img src={IMAGES.ckGroup} alt="Reservations" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-nala-black via-nala-black/60 to-nala-black/40" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <p className="text-nala-gold tracking-luxe uppercase text-sm mb-3">Reserve Your Experience</p>
          <h1 className="font-display text-5xl md:text-7xl text-gold-gradient">Reservations</h1>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          {/* OpenTable widget */}
          <Reveal>
            <div className="border border-nala-gold/20 bg-nala-charcoal/50 p-6 md:p-8">
              <h3 className="font-display text-2xl text-nala-cream mb-2">Book a Table</h3>
              <p className="text-nala-cream-muted text-sm mb-6">Reserve instantly with OpenTable — or call {BRAND.phone}</p>
              <div className="bg-nala-cream p-4 md:p-5 rounded-md">
                <OpenTableWidget height={480} />
              </div>
            </div>
          </Reveal>

          {/* Info */}
          <Reveal delay={120} className="space-y-8">
            <div>
              <h3 className="font-display text-2xl text-gold-gradient mb-5">Reservation Details</h3>
              <div className="space-y-5 text-nala-cream">
                <div className="flex items-start gap-4">
                  <Calendar className="text-nala-gold mt-0.5" size={20} />
                  <div><p className="font-medium">Advance Booking</p><p className="text-nala-cream-muted text-sm font-serif-lux text-base">We recommend booking 48 hours ahead for weekends.</p></div>
                </div>
                <div className="flex items-start gap-4">
                  <Users className="text-nala-gold mt-0.5" size={20} />
                  <div><p className="font-medium">Large Parties</p><p className="text-nala-cream-muted text-sm font-serif-lux text-base">For groups of 9+, call us for private table arrangements.</p></div>
                </div>
                <div className="flex items-start gap-4">
                  <Clock className="text-nala-gold mt-0.5" size={20} />
                  <div><p className="font-medium">Dress Code</p><p className="text-nala-cream-muted text-sm font-serif-lux text-base">Upscale attire. Come dressed to celebrate.</p></div>
                </div>
              </div>
            </div>
            <div className="border border-nala-gold/20 overflow-hidden">
              <img src={IMAGES.ckSuperior1} alt="VIP Cocktail" className="w-full h-64 object-cover" />
            </div>
          </Reveal>
        </div>

        {/* Bottle Packages */}
        <div id="THEREDROOM" className="mt-28">
          <SectionHeading eyebrow="Nightlife & VIP" title="Bottle Service Packages" subtitle="Elevate your night with prime seating and premium bottles." />
          <div className="grid md:grid-cols-3 gap-6 mt-16">
            {BOTTLE_PACKAGES.map((p, i) => (
              <Reveal key={p.name} delay={i * 90} className={`relative border p-8 flex flex-col ${i === 0 ? "border-nala-gold bg-nala-charcoal" : "border-nala-gold/20 bg-nala-charcoal/50"}`}>
                {i === 0 && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-nala-gold text-nala-black text-xs font-semibold px-4 py-1 tracking-wide-lux uppercase">Most Exclusive</span>
                )}
                <Crown className="text-nala-gold mb-4" size={26} />
                <p className="text-nala-gold text-xs uppercase tracking-[0.2em] mb-2">{p.tier}</p>
                <h3 className="font-display text-2xl text-nala-cream mb-3">{p.name}</h3>
                <p className="text-nala-cream-muted text-sm font-serif-lux text-base leading-relaxed mb-6">{p.desc}</p>
                <ul className="space-y-3 mb-8 flex-1">
                  {p.perks.map((perk) => (
                    <li key={perk} className="flex items-center gap-3 text-nala-cream text-sm">
                      <Check size={16} className="text-nala-gold shrink-0" /> {perk}
                    </li>
                  ))}
                </ul>
                <div className="mt-auto">
                  <p className="font-display text-xl text-gold-gradient mb-4">{p.price}</p>
                  <a href={BRAND.phoneHref} className={i === 0 ? "btn-gold w-full" : "btn-outline-gold w-full"}><span>Reserve Table</span></a>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
